import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import AssignRelationships from '../../../components/enrollments/AssignRelationships';
import EnrolCredentials from '../../../components/enrollments/EnrolCredentials';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { toast } from "react-toastify";
import UserCard from '../../../components/user-card/user-card';
import SettingTypesBasicFields from '../../../constants/setting-types-basic-fields';
import UserAuditTab from '../../../containers/user-details/user-audit-tab';
import UserDetailsAccessTabTwo from '../../../containers/user-details/user-details-access-tab';
import UserDetailsDetailsTab from '../../../containers/user-details/user-details-details-tab';
import UserTransactions from '../../../containers/user-details/userTransactions';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { CategoryFieldGroup } from '../../../models/CategoryFieldGroup';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { Person } from '../../../models/Person';
import { TabSummary } from '../../../models/TabSummary';
import { getCategoryFields } from '../../../services/customFieldsService';
import { getEnrolmentNames, getPersonByID, storePerson } from '../../../services/enrolmentService';
import { getUserTransactionReport } from '../../../services/reportService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { getTodayDate } from '../../../utilities/DateUtils';
import { appendFilter } from '../../../utilities/filters';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

// --> variables to update
const allItemsPath = '/access-control/enrolment';
const itemPath = '/access-control/enrolment/personnel/';
// <--

const Personnel: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, columnId: SettingTypesBasicFields.Display_Name.id, isActive: true },
        { label: translate.byKey(TranslationKeys.credentials), badge: 0, showInitial: false, columnId: SettingTypesBasicFields.Tags.id, isActive: false },
        { label: translate.byKey(TranslationKeys.access), badge: 0, showInitial: false, columnId: SettingTypesBasicFields.Access_Groups.id, isActive: false },
        { label: translate.byKey(TranslationKeys.relationships), badge: 0, showInitial: false, columnId: SettingTypesBasicFields.Links.id, isActive: false },
        { label: translate.byKey(TranslationKeys.transactions), badge: 0, showInitial: false, columnId: SettingTypesBasicFields.Links.id, isActive: false },
        { label: translate.byKey(TranslationKeys.audit), badge: 0, showInitial: false, columnId: SettingTypesBasicFields.Links.id, isActive: false },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: false, columnId: SettingTypesBasicFields.Status.id, isActive: false },

    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const initialPerson: Person = {
        id: +pageId,
        name: '',
        profileName: '',
        profileId: 0,
        mobileNumber: '',
        uid: '',
        email: '',
        pin: undefined,
        enableCredentials: true,
        apbOverride: false,
        images: [''],
        selectedImage: 1,
        totalTags: 0,
        totalAccessGroups: 0,
        totalFloors: 0,
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        birthday: undefined,
        suffix: '',
        gender: 'U',
        genderLabel: '',
        printTemplate: '',
        timeAndAttendance: false,
        company: '',
        department: '',
        birthdayId: 0,
        totalAssets: 0,
    }
    const [currentItem, setCurrentItem] = useState<Person>(initialPerson);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [supportedFields, setSupportedFields] = useState<CategoryFieldGroup[]>([]);
    const [accessGroupTotal, setAccessGroupTotal] = useState(0);
    const dialog = useDialog();

    const onAssignRelationships = (updateBadge: boolean, selectedIdsLength: number) => {
        if (updateBadge) {
            updateTabBadgeTotal(3, selectedIdsLength, tabs)
        }
    }

    const reloadPage = async () => {
        setBusy(true);
        if (itemId) {
            getPersonByID(translate, itemId)
                .then(async (newPerson) => {
                    setAccessGroupTotal(newPerson?.totalAccessGroups ?? 0);
                    // update the tab badge counts
                    setBreadcrumb([
                        { value: '/access-control/enrolment', displayName: translate.byKey(TranslationKeys.enrolments) },
                        { value: allItemsPath, displayName: `${newPerson?.category ?? ''}` },
                        { value: itemPath + `${itemId}`, displayName: `${newPerson?.name ?? ''}` }
                    ]);

                    // Get the list of fields supported by this category.
                    getCategoryFields(newPerson.profileId)
                        .then(async(resultFields) => {
                            const fieldsSupported = resultFields;
                            fieldsSupported.every((value, index) => addEmptyCustomFieldValues(value, index, newPerson))
                            setSelectedItem({label: newPerson.name, value: newPerson.id ? newPerson.id.toString() : ""})
                            setCurrentItem(newPerson);
                            setSupportedFields(fieldsSupported);

                            const updatedTabs =
                                updateTabBadgeTotal(1, newPerson?.totalCredentials,
                                    updateTabBadgeTotal(3, newPerson?.totalAssets,
                                        updateTabBadgeTotal(4, newPerson?.totalTransacksForToday,
                                            initialTabs
                                        )
                                    )
                                ).map(tab => itemId ? updateActiveTabs(tab, fieldsSupported[0]?.fields) : tab);
                            setTabs(updatedTabs);

                        })
                        .catch(error => {
                            showLoadItemError(error, dialog, translate, onBackToMainClick);
                        })
                }).catch(error => {
                    showLoadItemError(error, dialog, translate, onBackToMainClick);
                })
        }
        else {
            // Get the listof fields supported by the default category.
            let profileId = '16';

            if (sessionStorage.getItem('profileId') !== '0' && sessionStorage.getItem('profileId') !== null) {
                profileId = sessionStorage.getItem('profileId')?.toString() ?? profileId;
            }

            getCategoryFields(+profileId)
                .then((resultFields) => {
                    const fieldsSupported = resultFields;
                    setSupportedFields(fieldsSupported);
                })
            setTabs([initialTabs[0]]);
            setCurrentItem(initialPerson);
        }
        setBusy(false);
    }
    const onCredentialTabUpdate = async () => {
        try {
            const person = await getPersonByID(translate, itemId);
            setTabs(updateTabBadgeTotal(1, person?.totalCredentials ?? 0, tabs));
            reloadPage();  // we need revisit this issue
        } catch (error) {
            //We can swallow this error no need to show the user
            console.log(error);
        }
    }

 const getTransactionData = (listParameters?: ListQuery) => {
       return getUserTransactionReport(user.profileId, +itemId, translate, { ...listParameters, filters: appendFilter(`*${getTodayDate()}*`, "Date/Time", "trDatetimelocal", listParameters?.filters ?? []) });
    }

    /**
     * Content and descriptors for each tab
     */
    const tabContentList: ContentWrapper[] = [{
        content: <UserDetailsDetailsTab supportedFields={supportedFields} currentUser={currentItem}></UserDetailsDetailsTab >,
    },
    {
        content: <EnrolCredentials itemId={itemId} setBusy={setBusy} onCredentialTabUpdate={onCredentialTabUpdate} currentItem={currentItem} />
    },
    {
        content: <UserDetailsAccessTabTwo masterId={itemId} key={itemId} />
    },
    {
        content: <AssignRelationships itemId={itemId} setBusy={setBusy} siteId={currentItem.siteId} updateBadgeTotal={(updateBadge, selectedIdsLength) => { onAssignRelationships(updateBadge, selectedIdsLength) }} />,
    },
    {
        content: <UserTransactions masterId={itemId} dataMethod={getTransactionData} isTodayReportType={true} dataReloadMethod ={reloadPage} />
    },
    {
        content: <UserAuditTab masterId={+itemId} />
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }
    ];

    const topic: Topic = {
        accessor: 'mstDisplayname',
        title: translate.byKey(TranslationKeys.enrolments),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const updateActiveTabs = (tab, supportedFields) => {
        if (supportedFields && supportedFields.map(supportedField => supportedField.columnId).indexOf(tab.columnId) >= 0) {
            return { ...tab, isActive: true };
        }
        else {
            return { ...tab, isActive: false };
        }
    }

    const addEmptyCustomFieldValues = (value: CategoryFieldGroup, outerIndex: number, newPerson): true => {
        if (outerIndex > 0) { // Default fields are indexed to zero, we do not need to add them to initial fields.
            value.fields.every((categoryField, index) => {
                if (newPerson.custom) {
                    if (newPerson.custom[categoryField.systemName] === undefined) {
                        newPerson.custom[categoryField.systemName] = {
                            detailTypeId: categoryField.masterDetailTypeId,
                            isBoolean: false,
                            value: ''
                        };
                    }
                }
                else {
                    newPerson.custom = {
                        [categoryField.systemName]: {
                            detailTypeId: categoryField.masterDetailTypeId,
                            isBoolean: false,
                            value: ''
                        }
                    };
                }
                return true;
            });
        }
        return true;
    };



    const onBackToMainClick = () => {
        history.push(allItemsPath);
    }

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveEnrolment = (values) => {
        const profileId = sessionStorage.getItem('profileId') ?? '16';
        const user: Person = {
            ...currentItem,
            profileId: values.profileId || +profileId,
            id: +itemId,
            name: values.name,
            selectedImage: values.selectedImage,
            email: values.email,
            uid: values.uid,
            pin: values.pin,
            enableCredentials: values.enableCredentials,
            apbOverride: values.apbOverride,
            mobileNumber: values.mobileNumber,
            firstName: values.firstName,
            lastName: values.lastName,
            middleName: values.middleName,
            title: values.title,
            suffix: values.suffix,
            gender: values.gender,
            printTemplate: values.printTemplate,
            timeAndAttendance: values.timeAndAttendance,
            company: values.company,
            department: values.department,
            newImage: values.newImage,
            siteId: values.siteId ? values.siteId : siteId,
            masterImages: values.masterImages,
            description: values.description,
            custom: values.custom
        };
        const createNewEnrolment = itemId ? +itemId < 0 : true;
        const storeMethod = storePerson;

        saveItem(setBusy, storeMethod, user, createNewEnrolment, setItemId, history);
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId, accessGroupTotal])

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
        return tabContentList[0].content;
    };

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const saveItem = (setBusy, storeMethod, item, createNewItem, setItemId, history) => {
        setBusy(true);
        storeMethod(item, createNewItem)
            .then(async (value) => {
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.enrolmentSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                reloadPage();
                setBusy(false);
            });
    }

    const getDataMethodWithProfileFilter = (listParameter?: ListQuery) => {                           
        const profileId = Number(sessionStorage.getItem('profileId'));

        return profileId === 0 ? getEnrolmentNames(listParameter) : getEnrolmentNames({ ...listParameter, filters: appendFilter(profileId, 'Profile', 'profile.id', listParameter?.filters ?? []) })
    }

    return <TopicContextProvider topic={topic} dataMethod={getDataMethodWithProfileFilter} itemId={itemId} selectedItem={selectedItem}>
        <ItemContentTabTwo className="overflow-x-auto">
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={saveEnrolment}
                tabs={tabs}
                itemPath={itemPath}
                initialValues={currentItem}
                enableFieldClear = {true} // quick fix for clearing the data, this fix a should be improved
                >
                <UserCard supportedFields={supportedFields} />
            </SimpleItemDetails>
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default Personnel;
